// Снимки дня: отбор прибора, список снимков и шаг ‹ ›. Со сверкой с АИС —
// ещё набор разбора (все / пропуски / без АИС) и шаг по его элементам.

import { formatNumber, formatTime, useLang, useT } from '../i18n'
import { recordCount, sceneKey, setCount, type DatasetSet } from './datasetCalendar'
import type { DatasetScene } from './datasetLayer'
import type { DatasetSensor } from './imageryUrl'

interface Props {
  scenes: DatasetScene[]
  selected: string | null
  sensor: DatasetSensor
  onSensor: (s: DatasetSensor) => void
  set: DatasetSet
  /** Элементов набора за сутки (по выбранному прибору). */
  setTotals: Record<DatasetSet, number>
  onSet: (s: DatasetSet) => void
  onPick: (key: string) => void
  /** Шаг по снимкам (набор «все») или по элементам набора. */
  onStep: (delta: number) => void
  /** Позиция выбранного элемента набора: «3 из 12»; `null` — набор «все». */
  position: { i: number; n: number } | null
  /** Сколько снимков прибора без записей и показаны ли они (набор «все»). */
  empty: number
  showEmpty: boolean
  onShowEmpty: (on: boolean) => void
  /** Отметки без АИС ещё не записываются вовсе. */
  marksReady: boolean
  /** Сверка с АИС включена: наборы, их счётчики и скрытие снимков без записей. */
  sets: boolean
  /** Без сверки: число в строке снимка — отметки осмотров кабинета. */
  countOf?: (scene: DatasetScene) => number
}

const SENSORS: DatasetSensor[] = ['all', 'optical', 'radar']
export const SETS: DatasetSet[] = ['all', 'missed', 'noais']

const chip = (on: boolean) =>
  `flex-1 rounded-lg border px-1 py-0.5 text-[11px] ${
    on ? 'border-sonar text-foam' : 'border-hull text-mist hover:text-foam'}`

export function DayScenes({
  scenes, selected, sensor, onSensor, set, setTotals, onSet, onPick, onStep, position,
  empty, showEmpty, onShowEmpty, marksReady, sets, countOf,
}: Props) {
  const t = useT()
  const { lang } = useLang()
  return (
    <div>
      {sets && <div className="mb-1 flex items-center gap-1" title={t('imagery.ds.set.keys')}>
        {SETS.map((s, i) => (
          <button key={s} type="button" onClick={() => onSet(s)} className={chip(set === s)}>
            <span className="text-faint">{i + 1}</span> {t(`imagery.ds.set.${s}`)}
            {s !== 'all' && <span className="tabular-nums text-faint"> {setTotals[s]}</span>}
          </button>
        ))}
      </div>}
      <div className="mb-1 flex items-center gap-1">
        {SENSORS.map(s => (
          <button key={s} type="button" onClick={() => onSensor(s)} className={chip(sensor === s)}>
            {t(`imagery.ds.sensor.${s}`)}
          </button>
        ))}
        <button type="button" onClick={() => onStep(-1)} disabled={!scenes.length}
                aria-label={t(position ? 'imagery.ds.prevItem' : 'imagery.ds.prevScene')}
                className="rounded-lg border border-hull px-2 py-0.5 text-mist hover:text-foam">‹</button>
        {position && (
          <span className="text-[11px] tabular-nums text-mist">
            {t('imagery.ds.position', { i: position.i, n: position.n })}
          </span>
        )}
        <button type="button" onClick={() => onStep(1)} disabled={!scenes.length}
                aria-label={t(position ? 'imagery.ds.nextItem' : 'imagery.ds.nextScene')}
                className="rounded-lg border border-hull px-2 py-0.5 text-mist hover:text-foam">›</button>
      </div>
      {sets && set === 'all' && empty > 0 && (
        <label className="mb-1 flex items-center gap-1.5 text-[11px] text-mist">
          <input type="checkbox" checked={showEmpty} onChange={e => onShowEmpty(e.target.checked)} />
          {t('imagery.ds.emptyScenes', { n: empty })}
        </label>
      )}
      {set === 'noais' && !marksReady ? (
        <div className="text-[11px] text-mist">{t('imagery.ds.marksNotYet')}</div>
      ) : scenes.length === 0 ? (
        <div className="text-[11px] text-mist">
          {t(set !== 'all' ? `imagery.ds.none.${set}`
            : empty > 0 ? 'imagery.ds.noRecordScenes' : 'imagery.ds.noScenes')}
        </div>
      ) : (
        <div className="max-h-[220px] space-y-0.5 overflow-y-auto pr-1">
          {scenes.map(s => {
            const key = sceneKey(s)
            const n = !sets ? (countOf?.(s) ?? 0) : set === 'all' ? recordCount(s) : setCount(s, set)
            const label = !sets ? 'imagery.ds.count.sweep' : `imagery.ds.count.${set}`
            return (
              <button key={key} type="button" onClick={() => onPick(key)}
                      title={s.scene_id}
                      className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-0.5 text-left text-[11px] tabular-nums ${
                        selected === key ? 'bg-hull text-foam' : 'text-mist hover:bg-hull/50'}`}>
                <span className="w-10">{formatTime(lang, s.captured_at)}</span>
                <span className={`w-14 ${s.sensor === 'radar' ? 'text-violet-300' : 'text-sky-300'}`}>
                  {t(`imagery.ds.sensor.${s.sensor}`)}
                </span>
                <span className="w-14 whitespace-nowrap">
                  {s.cloud_pct_scene == null ? '' : `☁ ${formatNumber(lang, s.cloud_pct_scene, 0)} %`}
                </span>
                <span className="ml-auto">{n > 0 ? t(label, { n }) : ''}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
